import { memo, useState } from 'react'

import {
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  styled,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { DrmProviderConfig, DrmProviderConfigEncryptionSchemesEnum } from '@nativewaves/nw-cloud-sdk/content'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Controller, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Editor } from '@shared/components/Editor/EmbedEditor'
import { FormStructure } from 'domains/Workspaces/ContentFlows/AvcTasks/AvcTask/Task/Config/Outputs/Dialogs/Hls'
import { avcTaskConfigRoute } from 'pages/NWPlatform/Workspaces/Workspace/AvcTasks/AvcTask'
import { drmProviderQueryKeys } from 'services/queryKeys'

type ViewMode = 'ui' | 'json'

const DrmProviderWrapper = styled(Paper)(({ theme }) => ({
  gridColumn: '1 / span 3',
  margin: theme.spacing(-0.5),
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.levels[500],
}))

const JsonEditorBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  width: '100%',
  height: 300,
  display: 'flex',
}))

type DrmProviderSelectorProps = UseFormReturn<FormStructure>

const DrmProviderSelectorRaw = ({ control, watch, setValue }: DrmProviderSelectorProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const [viewMode, setViewMode] = useState<ViewMode>('ui')

  const { environmentId } = avcTaskConfigRoute.useParams()

  const drmProvidersQuery = useInfiniteQuery({
    ...drmProviderQueryKeys.list({ environmentId }),
    select: (data) => ({
      flat: data.pages.flatMap((page) => page.items),
    }),
  })

  const drmProviders = watch('drmProviders')
  const selectedProvider = drmProviders?.[0]

  const setSingleProvider = (next: Partial<DrmProviderConfig> | null) => {
    if (!next) {
      setValue('drmProviders', [], { shouldDirty: true })
      return
    }

    setValue(
      'drmProviders',
      [
        {
          disabled: false,
          encryptionSchemes: [DrmProviderConfigEncryptionSchemesEnum.Cbcs],
          ...selectedProvider,
          ...next,
        },
      ],
      { shouldDirty: true },
    )
  }

  return (
    <DrmProviderWrapper>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ mt: 0.5 }} variant="heading-base">
            {t('domain:Workspace.AvcTask.drmProvider')}
          </Typography>
          <Typography variant="text">{t('domain:Workspace.AvcTask.drmProviderInstruction')}</Typography>
        </Box>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_, value: ViewMode | null) => value && setViewMode(value)}
        >
          <ToggleButton value="ui">UI</ToggleButton>
          <ToggleButton value="json">JSON</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {viewMode === 'ui' ? (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl sx={{ flex: 1 }} size="small">
            <InputLabel id="drmProvider">{t('domain:Workspace.AvcTask.drmProvider')}</InputLabel>
            <Select
              labelId="drmProvider"
              value={selectedProvider?.drmProviderId ?? ''}
              input={<OutlinedInput label={t('domain:Workspace.AvcTask.drmProvider')} />}
              onChange={(event) =>
                setSingleProvider(event.target.value ? { drmProviderId: event.target.value } : null)
              }
            >
              <MenuItem value="">{t('none')}</MenuItem>
              {drmProvidersQuery.data?.flat.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={!!selectedProvider && !selectedProvider.disabled}
                disabled={!selectedProvider}
                onChange={(_, checked) => setSingleProvider({ disabled: !checked })}
              />
            }
            label={t('domain:Workspace.AvcTask.drmProviderEnabled')}
            labelPlacement="start"
          />
        </Box>
      ) : (
        <JsonEditorBox>
          <Controller
            control={control}
            name="drmProviders"
            render={({ field }) => <Editor value={field.value} onSubmit={(value) => field.onChange(value)} />}
          />
        </JsonEditorBox>
      )}
    </DrmProviderWrapper>
  )
}

export const DrmProviderSelector = memo(DrmProviderSelectorRaw)
